import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../lib/toast';
import { API, auth, errMsg, fmtDate, PrimaryBtn } from '../organization/finance/shared';

const PLAN_LABELS = { venue_trial: 'التجربة المجانية', venue_monthly: 'باقة تسيار للملاعب' };
const UPGRADE_STATUS_LABELS = { pending: 'طلبك قيد المراجعة من فريق تسيار', approved: 'تمت الموافقة! أصبح حسابك منشأة كاملة', rejected: 'تم رفض الطلب' };

export default function VenueOwnerSubscriptionPage() {
  const { club, trialStatus } = useOutletContext();
  const [plans, setPlans] = useState([]);
  const [upgradeRequest, setUpgradeRequest] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [plansRes, reqRes] = await Promise.all([
        axios.get(`${API}/saas/plans`, { params: { segment: 'venue_owner' } }),
        axios.get(`${API}/saas/venue-owner/upgrade-request`, auth()).catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data || []);
      setUpgradeRequest(reqRes.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestUpgrade = async () => {
    setRequesting(true);
    try {
      await axios.post(`${API}/saas/venue-owner/upgrade-request`, {}, auth());
      toast.success('تم إرسال طلب الترقية، سيراجعه فريق تسيار قريباً');
      load();
    } catch (e) { toast.error(errMsg(e, 'فشل إرسال الطلب')); } finally { setRequesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="venue-owner-subscription-page">
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="venue-owner-subscription-current-plan">
        <p className="text-xs font-semibold text-slate-500">باقتك الحالية</p>
        <p className="mt-1 text-xl font-black text-slate-900">{PLAN_LABELS[club?.subscription_plan] || club?.subscription_plan}</p>
        {trialStatus?.applicable && (
          <p className={`mt-2 text-sm font-semibold ${trialStatus.is_expired ? 'text-red-600' : 'text-orange-600'}`} data-testid="venue-owner-subscription-trial-info">
            {trialStatus.is_expired ? 'انتهت فترتك التجريبية' : `تجربة مجانية — ${trialStatus.days_remaining} يوم متبقي (تنتهي ${fmtDate(trialStatus.end_date)})`}
          </p>
        )}
      </div>

      <div>
        <p className="mb-3 text-sm font-bold text-slate-700">الباقات المتاحة</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {plans.map((p) => (
            <div key={p.plan_id} className={`rounded-2xl border-2 p-5 ${p.is_recommended ? 'border-orange-400 bg-orange-50/40' : 'border-slate-200 bg-white'}`} data-testid={`venue-owner-plan-card-${p.plan_id}`}>
              {p.badge_text && <span className="mb-2 inline-block rounded-full bg-orange-600 px-2.5 py-0.5 text-[11px] font-bold text-white">{p.badge_text}</span>}
              <p className="font-bold text-slate-900">{p.display_name}</p>
              <p className="mt-1 text-2xl font-black text-slate-900">{p.price} <span className="text-sm font-semibold text-slate-500">ر.س / {p.billing_period}</span></p>
              <ul className="mt-3 space-y-1 text-xs text-slate-600">
                {p.features.map((f, i) => <li key={i}>• {f}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">لترقية باقتك أو تعديلها، تواصل مع فريق تسيار عبر المحادثة المباشرة في التطبيق.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="venue-owner-upgrade-section">
        <p className="text-sm font-bold text-slate-700">الترقية إلى منشأة كاملة</p>
        <p className="mt-1 text-xs text-slate-500">تحتاج فرقاً وموظفين ولاعبين وحوكمة كاملة؟ اطلب ترقية حسابك إلى منشأة كاملة — بلا فقد أي بيانات، ملاعبك وحجوزاتك وعملاؤك تبقى كما هي.</p>
        {upgradeRequest ? (
          <p className={`mt-3 text-sm font-bold ${upgradeRequest.status === 'approved' ? 'text-emerald-600' : upgradeRequest.status === 'rejected' ? 'text-red-600' : 'text-amber-600'}`} data-testid="venue-owner-upgrade-status">
            {UPGRADE_STATUS_LABELS[upgradeRequest.status]}
          </p>
        ) : (
          <div className="mt-3">
            <PrimaryBtn onClick={requestUpgrade} disabled={requesting} data-testid="venue-owner-upgrade-request-btn">
              {requesting ? 'جارٍ الإرسال...' : 'طلب الترقية إلى منشأة كاملة'}
            </PrimaryBtn>
          </div>
        )}
      </div>
    </div>
  );
}
